import type { ModelData } from '@/types/models';
import { formatCurrency, formatNumber } from '@/utils/formatters';

interface MetricsTabProps {
  data: ModelData;
}

export function MetricsTab({ data }: MetricsTabProps) {
  const metrics = data?.metrics;
  const years = data?.years || [];
  const revenue = data?.revenue || [];
  const pnl = data?.pnl || [];

  // Handle case where data is not yet loaded
  if (!metrics && !years.length) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="text-4xl mb-3">📊</div>
          <p className="text-slate-500">No metrics data available</p>
        </div>
      </div>
    );
  }

  // Helper to get value from metrics (handles both single values and arrays)
  const getMetricValue = (value: number | number[] | undefined): number | undefined => {
    if (value === undefined || value === null) return undefined;
    if (Array.isArray(value)) {
      return value[value.length - 1];
    }
    return value;
  };

  // Format metric value based on type
  const formatMetric = (value: number | number[] | undefined, type: 'currency' | 'number' | 'percentage' | 'ratio') => {
    const displayValue = getMetricValue(value);
    
    if (displayValue === undefined || isNaN(displayValue)) return '-';
    
    switch (type) {
      case 'currency':
        return formatCurrency(displayValue);
      case 'percentage':
        return `${(displayValue * 100).toFixed(1)}%`;
      case 'ratio':
        return displayValue.toFixed(2);
      default:
        return formatNumber(displayValue);
    }
  };

  // Calculate Year 5 values
  const year5Revenue = revenue[revenue.length - 1]?.total || 0;
  const year5NetIncome = pnl[pnl.length - 1]?.netIncome || 0;
  const year5Ebitda = pnl[pnl.length - 1]?.ebitda || 0;

  // Calculate CAGR
  const year1Revenue = revenue[0]?.total || 1;
  const cagr = years.length > 1 
    ? (Math.pow(year5Revenue / year1Revenue, 1 / (years.length - 1)) - 1) * 100
    : 0;

  const mrrValue = getMetricValue(metrics?.mrr);
  const arrValue = getMetricValue(metrics?.arr);
  const cacValue = getMetricValue(metrics?.cac);
  const ltvValue = getMetricValue(metrics?.ltv);
  const ltvCacRatioValue = getMetricValue(metrics?.ltvCacRatio);
  const grossMarginValue = getMetricValue(metrics?.grossMargin);
  const ebitdaMarginValue = getMetricValue(metrics?.ebitdaMargin);

  return (
    <div className="space-y-8">
      {/* Key Metrics Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {mrrValue !== undefined && (
          <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-4 rounded-lg border border-blue-200">
            <div className="text-sm text-blue-600 font-medium">Monthly Recurring Revenue</div>
            <div className="text-2xl font-bold text-blue-900 mt-1">
              {formatCurrency(mrrValue)}
            </div>
          </div>
        )}
        
        {arrValue !== undefined && (
          <div className="bg-gradient-to-br from-green-50 to-green-100 p-4 rounded-lg border border-green-200">
            <div className="text-sm text-green-600 font-medium">Annual Recurring Revenue</div>
            <div className="text-2xl font-bold text-green-900 mt-1">
              {formatCurrency(arrValue)}
            </div>
          </div>
        )}
        
        {cacValue !== undefined && (
          <div className="bg-gradient-to-br from-orange-50 to-orange-100 p-4 rounded-lg border border-orange-200">
            <div className="text-sm text-orange-600 font-medium">Customer Acquisition Cost</div>
            <div className="text-2xl font-bold text-orange-900 mt-1">
              {formatCurrency(cacValue)}
            </div>
          </div>
        )}
        
        {ltvValue !== undefined && (
          <div className="bg-gradient-to-br from-purple-50 to-purple-100 p-4 rounded-lg border border-purple-200">
            <div className="text-sm text-purple-600 font-medium">Lifetime Value</div>
            <div className="text-2xl font-bold text-purple-900 mt-1">
              {formatCurrency(ltvValue)}
            </div>
          </div>
        )}
        
        {ltvCacRatioValue !== undefined && (
          <div className="bg-gradient-to-br from-indigo-50 to-indigo-100 p-4 rounded-lg border border-indigo-200">
            <div className="text-sm text-indigo-600 font-medium">LTV:CAC Ratio</div>
            <div className="text-2xl font-bold text-indigo-900 mt-1">
              {ltvCacRatioValue.toFixed(2)}x
            </div>
          </div>
        )}
        
        {grossMarginValue !== undefined && (
          <div className="bg-gradient-to-br from-teal-50 to-teal-100 p-4 rounded-lg border border-teal-200">
            <div className="text-sm text-teal-600 font-medium">Gross Margin</div>
            <div className="text-2xl font-bold text-teal-900 mt-1">
              {formatMetric(grossMarginValue, 'percentage')}
            </div>
          </div>
        )}
        
        {ebitdaMarginValue !== undefined && (
          <div className={`bg-gradient-to-br p-4 rounded-lg border ${
            ebitdaMarginValue >= 0
              ? 'from-emerald-50 to-emerald-100 border-emerald-200'
              : 'from-red-50 to-red-100 border-red-200'
          }`}>
            <div className={`text-sm font-medium ${
              ebitdaMarginValue >= 0 ? 'text-emerald-600' : 'text-red-600'
            }`}>EBITDA Margin</div>
            <div className={`text-2xl font-bold mt-1 ${
              ebitdaMarginValue >= 0 ? 'text-emerald-900' : 'text-red-900'
            }`}>
              {formatMetric(ebitdaMarginValue, 'percentage')}
            </div>
          </div>
        )}
      </div>

      {/* Summary Table */}
      <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
        <div className="bg-slate-50 px-4 py-3 border-b border-slate-200">
          <h3 className="font-semibold text-slate-800">5-Year Summary</h3>
        </div>
        <table className="w-full text-sm">
          <tbody className="divide-y divide-slate-100">
            <tr className="hover:bg-slate-50">
              <td className="py-3 px-4 font-medium text-slate-700">Year 5 Revenue</td>
              <td className="py-3 px-4 text-right font-semibold text-slate-900">
                {formatCurrency(year5Revenue)}
              </td>
            </tr>
            <tr className="hover:bg-slate-50">
              <td className="py-3 px-4 font-medium text-slate-700">Revenue CAGR</td>
              <td className={`py-3 px-4 text-right font-semibold ${cagr >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {isNaN(cagr) ? '-' : `${cagr.toFixed(1)}%`}
              </td>
            </tr>
            <tr className="hover:bg-slate-50">
              <td className="py-3 px-4 font-medium text-slate-700">Year 5 EBITDA</td>
              <td className={`py-3 px-4 text-right font-semibold ${year5Ebitda >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {formatCurrency(year5Ebitda)}
              </td>
            </tr>
            <tr className="hover:bg-slate-50">
              <td className="py-3 px-4 font-medium text-slate-700">Year 5 Net Income</td>
              <td className={`py-3 px-4 text-right font-semibold ${year5NetIncome >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {formatCurrency(year5NetIncome)}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}
